/**
 * PlatformUsers Page
 *
 * Platform Management: View and manage users across all organizations
 * Create superadmin users (must have @harriscomputer.com email)
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  UserGroupIcon,
  PlusIcon,
  ShieldCheckIcon,
  BuildingOfficeIcon,
  EnvelopeIcon,
  PencilIcon,
  PencilSquareIcon,
  MagnifyingGlassIcon,
  TrashIcon,
  ArrowPathIcon,
  ClockIcon,
  XCircleIcon,
  PaperAirplaneIcon,
  CheckCircleIcon,
  KeyIcon
} from '@heroicons/react/24/outline';
import { getRoleDisplayName, getRoleBadgeClasses, roleFilterOptionsForCaller } from '@/utils/roleMapping';
import { apiErrorMessage } from '@/utils/apiErrorMessage';
import { useAuth } from '@/hooks/useAuth';
import { canUseConsoleAction, canResendInvitation, canCancelInvitation, canEditInvitation, invitationRenewAction } from '@/utils/consoleActionAccess';
import { DeactivateConfirmModal } from '@/components/deletion/DeactivateConfirmModal';
import { UserModal, User as UserType, type EditableInvitation } from '../components/management/UserModal';
import { PageTransition } from '@/components/ui/PageTransition';
import { GradientAvatar, RoleBadge, StatusBadge, getUserDisplayRoles, UserData } from '../components/users';
import { DataTable, ColumnDefinition, ActionDefinition, SortState } from '@/components/shared/DataTable';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from 'react-hot-toast';

// Re-export User type for other components
export type User = UserType;
import { ConfirmationDialog } from '../components/ui/ConfirmationDialog';
import { BulkActionsBar } from '../components/management/BulkActionsBar';
import { AuditHistoryTimeline } from '../components/management/AuditHistoryTimeline';
import { UserExport } from '../components/management/UserExport';
import { EmptyState } from '../components/ui/EmptyState';
import { RestoreUserDialog, DeletedUserInfo } from '../components/management/RestoreUserDialog';
import { ResendInvitationDialog, InvitationInfo } from '../components/management/ResendInvitationDialog';

import axios from 'axios';
import { apiService } from '@/services/api';
import { API_BASE_URL } from '../config/api';

// Origin without the /api suffix API_BASE_URL carries, because the raw fetch calls below build
// their own /api/v1 paths. Read through the shared config rather than import.meta so this module
// is loadable outside Vite.
const API_URL = API_BASE_URL.replace(/\/api$/, '');

interface Organization {
  id: string;
  name: string;
  partnerId?: string;
}

interface InvitedBy {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
}

// Extended User type that can also represent invitations
type UserOrInvitation = User & {
  type: 'user' | 'invitation';
  invitationId?: string;
  invitedBy?: InvitedBy;
  expiresAt?: string;
  invitationStatus?: 'pending' | 'expired' | 'cancelled';
};

interface Enterprise {
  id: string;
  name: string;
}

interface Partner {
  id: string;
  name: string;
  enterpriseId?: string;
}

// Raw shape returned by the enriched GET /invitations endpoint
interface RawInvitation {
  id: string;
  email: string;
  role: string;
  organizationId?: string | { id: string; name: string };
  invitedBy?: string | { firstName?: string; lastName?: string };
  createdAt: string;
  expiresAt: string;
  status: 'pending' | 'accepted' | 'cancelled' | 'expired';
  acceptedAt?: string;
  canManage?: boolean;
}

// Flat invitation type used by the dedicated Invitations tab
interface InvitationRecord {
  id: string;
  email: string;
  role: string;
  organizationId: string;
  organizationName?: string;
  invitedByName?: string;
  createdAt: string;
  expiresAt: string;
  status: 'pending' | 'accepted' | 'cancelled' | 'expired';
  acceptedAt?: string;
  /**
   * The list's answer to whether this caller passes the sender check on edit and cancel: for an
   * enterprise admin, the invitation was sent from inside its enterprise. Only `false` withholds
   * the controls; the server decides either way.
   */
  canManage?: boolean;
}

const byName = <T extends { name: string }>(a: T, b: T): number =>
  (a.name || '').localeCompare(b.name || '');

type InvitationSendState = 'idle' | 'sending' | 'sent';

/**
 * The send control on an invitation row.
 *
 * Disabled while its request is in flight, because the endpoint has no idempotency key and a second
 * click is a second email. The check is the row's own confirmation: the toast may have faded, and the
 * row is where the reader is looking.
 */
const InvitationSendButton: React.FC<{
  state: InvitationSendState;
  titles: { idle: string; sending: string; sent: string };
  onClick: () => void;
}> = ({ state, titles, onClick }) => {
  const title = titles[state];

  return (
    <motion.button
      type="button"
      whileTap={state === 'idle' ? { scale: 0.9 } : undefined}
      onClick={onClick}
      disabled={state !== 'idle'}
      title={title}
      aria-label={title}
      aria-busy={state === 'sending'}
      className={`inline-flex items-center p-1.5 rounded disabled:cursor-default ${
        state === 'sent'
          ? 'text-green-600'
          : 'text-blue-600 hover:text-blue-800 hover:bg-blue-50 disabled:hover:bg-transparent'
      }`}
    >
      {state === 'sending' && <ArrowPathIcon className="h-4 w-4 animate-spin" />}
      {state === 'sent' && <CheckCircleIcon className="h-4 w-4" />}
      {state === 'idle' && <PaperAirplaneIcon className="h-4 w-4" />}
    </motion.button>
  );
};

const PlatformUsers: React.FC = () => {
  const { user, isSuperAdmin, hasPermission } = useAuth();
  // The enterprise and partner lists are permission-gated endpoints a channel partner admin does not
  // hold. Fetching them regardless produced an "Access denied" toast per request, since the axios
  // interceptor reports every 403 before the caller's own error handling runs.
  const canViewEnterprises = hasPermission('platform.enterprises.view');
  const canViewPartners = hasPermission('platform.partners.view');
  const canInviteUser = canUseConsoleAction('inviteUser', { role: user?.role, hasPermission });
  const roleFilterOptions = roleFilterOptionsForCaller(user?.role);
  const [users, setUsers] = useState<UserOrInvitation[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [enterprises, setEnterprises] = useState<Enterprise[]>([]);
  const [partners, setPartners] = useState<Partner[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deactivateTarget, setDeactivateTarget] = useState<{ id: string; email: string } | null>(null);
  const [isDeactivating, setIsDeactivating] = useState(false);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout>>();
  const [debouncedSearch, setDebouncedSearch] = useState<string>('');

  // Modal states
  const [modalMode, setModalMode] = useState<'create' | 'edit' | 'edit-invitation'>('create');
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [selectedInvitation, setSelectedInvitation] = useState<EditableInvitation | null>(null);

  // Delete/Reactivate state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingUser, setDeletingUser] = useState<User | null>(null);
  const [showReactivateConfirm, setShowReactivateConfirm] = useState(false);
  const [reactivatingUser, setReactivatingUser] = useState<User | null>(null);
  const [showDeleted, setShowDeleted] = useState(false);

  // Audit History state
  const [showAuditHistory, setShowAuditHistory] = useState(false);
  const [auditUser, setAuditUser] = useState<User | null>(null);

  // Restore deleted user dialog state
  const [showRestoreDialog, setShowRestoreDialog] = useState(false);
  const [restoreUserInfo, setRestoreUserInfo] = useState<DeletedUserInfo | null>(null);
  const [restoreLoading, setRestoreLoading] = useState(false);

  // Resend invitation dialog state
  const [showResendDialog, setShowResendDialog] = useState(false);
  const [resendInvitationInfo, setResendInvitationInfo] = useState<InvitationInfo | null>(null);
  const [resendLoading, setResendLoading] = useState(false);

  // Bulk selection state
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());

  // Filters
  const [filterRole, setFilterRole] = useState<string>('all');
  const [filterOrganization, setFilterOrganization] = useState<string>('all');
  const [filterEnterprise, setFilterEnterprise] = useState<string>('all');
  const [filterPartner, setFilterPartner] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all'); // all, active, inactive
  const [filterActivity, setFilterActivity] = useState<string>('all'); // all, 7days, 30days, 90days
  const [filterCredentials, setFilterCredentials] = useState<string>('all'); // all, hasLicense, noLicense
  const [filterNoExternalId, setFilterNoExternalId] = useState<boolean>(false);
  const [filterExpiredCredentials, setFilterExpiredCredentials] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Pagination
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0 });

  // Sorting
  const [sortState, setSortState] = useState<SortState>({
    column: 'firstName',
    direction: 'asc',
  });

  // Active tab
  const [activeTab, setActiveTab] = useState<string>('users');

  // Invitations tab state
  // One row's send at a time: the control is disabled while its request is in flight, so a second
  // click cannot send a second email. `justSent` is the row's own confirmation, which outlives the
  // refetch because it is keyed by invitation id.
  const [invitationInFlight, setInvitationInFlight] = useState<string | null>(null);
  // The latch is the ref, not the state: three clicks in one batch all read the same stale state, so
  // the guard has to see the first one's effect synchronously.
  const invitationInFlightRef = useRef<string | null>(null);
  const [invitationJustSent, setInvitationJustSent] = useState<string | null>(null);
  const invitationSentTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [invitationRecords, setInvitationRecords] = useState<InvitationRecord[]>([]);
  const [invitationPagination, setInvitationPagination] = useState({ total: 0, page: 1, limit: 20, totalPages: 0 });
  const [invitationFilters, setInvitationFilters] = useState({
    status: 'all',
    organizationId: 'all',
    partnerId: 'all',
    role: 'all',
    search: '',
  });
  const [selectedInvitationIds, setSelectedInvitationIds] = useState<Set<string>>(new Set());
  const [loadingInvitations, setLoadingInvitations] = useState(false);
  const invSearchDebounceRef = useRef<ReturnType<typeof setTimeout>>();
  const [debouncedInvSearch, setDebouncedInvSearch] = useState<string>('');

  // Get filters from URL query params on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const orgId = params.get('organizationId');
    if (orgId) {
      setFilterOrganization(orgId);
    }
    if (params.get('noExternalId') === 'true') {
      setFilterNoExternalId(true);
    }
    if (params.get('expiredCredentials') === 'true') {
      setFilterExpiredCredentials(true);
    }
  }, []);

  // Debounce search input — update debouncedSearch after 300ms
  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => { if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current); };
  }, [searchQuery]);

  // Debounce invitation search
  useEffect(() => {
    if (invSearchDebounceRef.current) clearTimeout(invSearchDebounceRef.current);
    invSearchDebounceRef.current = setTimeout(() => {
      setDebouncedInvSearch(invitationFilters.search);
    }, 300);
    return () => { if (invSearchDebounceRef.current) clearTimeout(invSearchDebounceRef.current); };
  }, [invitationFilters.search]);

  // AbortController refs for race condition prevention
  const usersAbortRef = useRef<AbortController | null>(null);
  const invitationsAbortRef = useRef<AbortController | null>(null);

  // Cleanup abort controllers on unmount
  useEffect(() => {
    return () => {
      usersAbortRef.current?.abort();
      invitationsAbortRef.current?.abort();
    };
  }, []);

  // Fetch users when any server-side filter/pagination/sort changes
  // Merged with page-reset logic: if filters changed and not on page 1, reset page first
  const prevFiltersRef = useRef<string>('');
  useEffect(() => {
    const filterKey = JSON.stringify({
      filterRole, filterOrganization, filterEnterprise, filterPartner,
      filterStatus, filterActivity, filterCredentials, filterNoExternalId,
      filterExpiredCredentials, debouncedSearch, showDeleted
    });
    const filtersChanged = prevFiltersRef.current !== '' && prevFiltersRef.current !== filterKey;
    prevFiltersRef.current = filterKey;

    // If filters changed and not on page 1, reset page (will re-trigger this effect with page=1)
    if (filtersChanged && pagination.page !== 1) {
      setPagination(prev => ({ ...prev, page: 1 }));
      return;
    }

    fetchUsers();
  }, [
    pagination.page, pagination.limit, filterRole, filterOrganization, filterEnterprise,
    filterPartner, filterStatus, filterActivity, filterCredentials, filterNoExternalId,
    filterExpiredCredentials, debouncedSearch, sortState.column, sortState.direction, showDeleted
  ]);

  // Fetch invitations when invitation tab is active and filters/page change
  const prevInvFiltersRef = useRef<string>('');
  useEffect(() => {
    if (activeTab !== 'invitations') return;

    const filterKey = JSON.stringify({
      status: invitationFilters.status, organizationId: invitationFilters.organizationId,
      partnerId: invitationFilters.partnerId, role: invitationFilters.role, debouncedInvSearch
    });
    const filtersChanged = prevInvFiltersRef.current !== '' && prevInvFiltersRef.current !== filterKey;
    prevInvFiltersRef.current = filterKey;

    if (filtersChanged && invitationPagination.page !== 1) {
      setInvitationPagination(prev => ({ ...prev, page: 1 }));
      return;
    }

    fetchInvitations();
  }, [
    activeTab, invitationFilters.status, invitationFilters.organizationId,
    invitationFilters.partnerId, invitationFilters.role, debouncedInvSearch,
    invitationPagination.page
  ]);

  // Fetch reference data on mount. Both hierarchy filters hide themselves when their list is empty,
  // so skipping the fetch a role is not authorized for costs nothing visible.
  useEffect(() => {
    fetchOrganizations();
    if (canViewEnterprises) fetchEnterprises();
    if (canViewPartners) fetchPartners();
  }, [canViewEnterprises, canViewPartners]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'a' && !showModal && !showDeleteConfirm && !showReactivateConfirm && !showAuditHistory) {
        e.preventDefault();
        handleSelectAll();
      }
      if (e.key === 'Escape') {
        if (showModal) setShowModal(false);
        else if (showDeleteConfirm) setShowDeleteConfirm(false);
        else if (showReactivateConfirm) setShowReactivateConfirm(false);
        else if (showAuditHistory) setShowAuditHistory(false);
        else if (selectedUserIds.size > 0) setSelectedUserIds(new Set());
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [users, selectedUserIds, showModal, showDeleteConfirm, showReactivateConfirm, showAuditHistory]);

  // Cascade filter logic: filter partners by selected enterprise
  const filteredPartners = useMemo(() => {
    if (filterEnterprise === 'all') return partners;
    return partners.filter(p => p.enterpriseId === filterEnterprise);
  }, [partners, filterEnterprise]);

  // Cascade filter logic: filter organizations by selected partner
  const filteredOrganizations = useMemo(() => {
    if (filterPartner === 'all') return organizations;
    return organizations.filter(o => o.partnerId === filterPartner);
  }, [organizations, filterPartner]);

  // Cascade filter logic for invitations tab: filter orgs by selected invitation partner
  const filteredInvOrganizations = useMemo(() => {
    if (invitationFilters.partnerId === 'all') return organizations;
    return organizations.filter(o => o.partnerId === invitationFilters.partnerId);
  }, [organizations, invitationFilters.partnerId]);

  // Auto-clear: if selected partner is no longer in filtered list after enterprise changes
  useEffect(() => {
    if (filterPartner !== 'all' && !filteredPartners.find(p => p.id === filterPartner)) {
      setFilterPartner('all');
    }
  }, [filterEnterprise]); // intentionally narrow: only re-run when enterprise changes

  // Auto-clear: if selected org is no longer in filtered list after partner changes
  useEffect(() => {
    if (filterOrganization !== 'all' && !filteredOrganizations.find(o => o.id === filterOrganization)) {
      setFilterOrganization('all');
    }
  }, [filterPartner]); // intentionally narrow: only re-run when partner changes

  // Auto-clear: if invitation org is no longer valid after invitation partner changes
  useEffect(() => {
    if (invitationFilters.organizationId !== 'all' && !filteredInvOrganizations.find(o => o.id === invitationFilters.organizationId)) {
      setInvitationFilters(prev => ({ ...prev, organizationId: 'all' }));
    }
  }, [invitationFilters.partnerId]); // intentionally narrow: only re-run when invitation partner changes

  const handlePageChange = (page: number) => {
    setPagination(prev => ({ ...prev, page }));
  };

  const handleLimitChange = (limit: number) => {
    setPagination(prev => ({ ...prev, limit, page: 1 }));
  };

  const fetchOrganizations = async () => {
    try {
      const result = await apiService.get<{ success: boolean; data: any[] }>('/v1/platform/organizations');
      if (result.success && result.data) {
        setOrganizations(result.data.map((o: any) => ({ id: o.id, name: o.name, partnerId: o.partnerId })).sort(byName));
      }
    } catch (err) {
      console.error('Error fetching organizations:', err);
    }
  };

  const fetchEnterprises = async () => {
    try {
      const result = await apiService.get<{ success: boolean; data: any }>('/v1/platform/enterprises');
      if (result.success && result.data) {
        const list = Array.isArray(result.data) ? result.data : result.data.enterprises || [];
        setEnterprises(list.map((e: any) => ({ id: e.id, name: e.name })).sort(byName));
      }
    } catch (err) {
      console.error('Error fetching enterprises:', err);
    }
  };

  const fetchPartners = async () => {
    try {
      const result = await apiService.get<{ success: boolean; data: any[] }>('/v1/platform/partners');
      if (result.success && result.data) {
        setPartners(result.data.map((p: any) => ({ id: p.id, name: p.name, enterpriseId: p.enterpriseId })).sort(byName));
      }
    } catch (err) {
      console.error('Error fetching partners:', err);
    }
  };

  const fetchUsers = async () => {
    // Abort any in-flight request to prevent race conditions
    usersAbortRef.current?.abort();
    usersAbortRef.current = new AbortController();

    try {
      setLoading(true);
      setError(null);

      // Build server-side query params
      const params: Record<string, string> = {
        page: String(pagination.page),
        limit: String(pagination.limit),
      };
      if (filterRole !== 'all') params.role = filterRole;
      if (filterOrganization !== 'all') params.organizationId = filterOrganization;
      if (filterEnterprise !== 'all') params.enterpriseId = filterEnterprise;
      if (filterPartner !== 'all') params.partnerId = filterPartner;
      if (filterStatus !== 'all') params.status = filterStatus;
      if (showDeleted) params.includeDeleted = 'true';
      if (debouncedSearch.trim()) params.search = debouncedSearch.trim();
      if (filterActivity !== 'all') {
        const daysMap: Record<string, string> = { '7days': '7', '30days': '30', '90days': '90' };
        if (daysMap[filterActivity]) params.activityDays = daysMap[filterActivity];
      }
      if (filterCredentials === 'hasLicense') params.hasLicense = 'true';
      if (filterCredentials === 'noLicense') params.hasLicense = 'false';
      if (filterNoExternalId) params.noExternalId = 'true';
      if (filterExpiredCredentials) params.expiredCredentials = 'true';
      // Map sort column to backend field name
      const sortMap: Record<string, string> = {
        user: 'firstName', firstName: 'firstName', email: 'email',
        role: 'role', organization: 'organization', status: 'status', created: 'createdAt'
      };
      if (sortState.column) params.sortBy = sortMap[sortState.column] || 'createdAt';
      if (sortState.direction) params.sortOrder = sortState.direction;

      const usersResult = await apiService.get<{ success: boolean; data: any[]; pagination?: any }>(
        '/v1/platform/users',
        params,
        { signal: usersAbortRef.current.signal }
      );

      if (!usersResult.success || !usersResult.data) {
        throw new Error('Failed to load users');
      }

      // Set paginated users (already sorted/filtered by server)
      const usersData: UserOrInvitation[] = usersResult.data.map((user: User) => ({
        ...user,
        type: 'user' as const
      }));
      setUsers(usersData);

      // Update pagination from server response
      if (usersResult.pagination) {
        setPagination(prev => ({
          ...prev,
          total: usersResult.pagination.total,
          page: usersResult.pagination.page,
        }));
      }
    } catch (err: any) {
      if (axios.isCancel(err) || (err instanceof DOMException && err.name === 'AbortError')) return;
      console.error('Error fetching users:', err);
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const fetchInvitations = async () => {
    // Abort any in-flight request to prevent race conditions
    invitationsAbortRef.current?.abort();
    invitationsAbortRef.current = new AbortController();

    setLoadingInvitations(true);
    try {
      const params: Record<string, string> = {
        page: String(invitationPagination.page),
        limit: '20',
      };
      if (invitationFilters.status !== 'all') params.status = invitationFilters.status;
      if (invitationFilters.organizationId !== 'all') params.organizationId = invitationFilters.organizationId;
      if (invitationFilters.partnerId !== 'all') params.partnerId = invitationFilters.partnerId;
      if (invitationFilters.role !== 'all') params.role = invitationFilters.role;
      if (debouncedInvSearch) params.search = debouncedInvSearch;

      const result = await apiService.get<{ success: boolean; data: any[]; pagination?: any }>(
        '/v1/platform/invitations',
        params,
        { signal: invitationsAbortRef.current.signal }
      );

      if (!result.success) {
        throw new Error('Failed to load invitations');
      }

      // Flatten the enriched invitation shape from the API
      const records: InvitationRecord[] = (result.data || []).map((inv: RawInvitation) => ({
        id: inv.id,
        email: inv.email,
        role: inv.role,
        organizationId: (typeof inv.organizationId === 'object' ? inv.organizationId?.id : inv.organizationId) || '',
        organizationName: typeof inv.organizationId === 'object' ? inv.organizationId?.name : undefined,
        invitedByName: typeof inv.invitedBy === 'object'
          ? `${inv.invitedBy?.firstName ?? ''} ${inv.invitedBy?.lastName ?? ''}`.trim()
          : undefined,
        createdAt: inv.createdAt,
        expiresAt: inv.expiresAt,
        status: inv.status,
        acceptedAt: inv.acceptedAt,
        canManage: inv.canManage,
      }));

      setInvitationRecords(records);
      if (result.pagination) {
        setInvitationPagination(result.pagination);
      }
    } catch (err) {
      if (axios.isCancel(err) || (err instanceof DOMException && err.name === 'AbortError')) return;
      console.error('Error fetching invitations:', err);
    } finally {
      setLoadingInvitations(false);
    }
  };

  const handleEditUser = (user: User) => {
    setModalMode('edit');
    setEditingUser(user);
    setShowModal(true);
  };

  const handleCreateClick = () => {
    setModalMode('create');
    setEditingUser(null);
    setShowModal(true);
  };

  const handleSaveUser = async (user: User) => {
    // edit-invitation mode: UserModal already persisted the change via invitations.service.
    // All we need to do here is refresh the invitations list.
    if (modalMode === 'edit-invitation') {
      await fetchInvitations();
      return;
    }

    const token = localStorage.getItem('accessToken');
    if (!token) {
      throw new Error('Authentication required');
    }

    const isCreate = !user.id;
    const url = isCreate
      ? `${API_URL}/api/v1/platform/users/invite`
      : `${API_URL}/api/v1/platform/users/${user.id}`;

    const method = isCreate ? 'POST' : 'PUT';

    const response = await fetch(url, {
      method,
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(user)
    });

    const result = await response.json();

    if (!response.ok) {
      // Handle specific error codes for better UX
      if (result.code === 'USER_DELETED' && result.data) {
        // User was deleted - offer to restore
        setRestoreUserInfo({
          userId: result.data.userId,
          email: result.data.email,
          originalRole: result.data.originalRole,
          firstName: result.data.firstName,
          lastName: result.data.lastName,
          deletedAt: result.data.deletedAt,
          organizationId: result.data.organizationId,
          organizationName: result.data.organizationName,
        });
        setShowRestoreDialog(true);
        setShowModal(false);
        return; // Don't throw - let dialog handle it
      }

      if (result.code === 'USER_INACTIVE' && result.data) {
        // User is inactive - suggest reactivation
        throw new Error(`An inactive user exists with this email (${result.data.email}). Please use the "Reactivate" action instead.`);
      }

      if ((result.code === 'INVITATION_PENDING' || result.code === 'INVITATION_EXPIRED') && result.data) {
        // Invitation exists - offer to resend/revive
        setResendInvitationInfo({
          invitationId: result.data.invitationId,
          email: result.data.email,
          role: result.data.role,
          expiresAt: result.data.expiresAt,
          expiryHours: result.data.expiryHours,
          organizationId: result.data.organizationId,
          organizationName: result.data.organizationName,
          isExpired: result.code === 'INVITATION_EXPIRED',
        });
        setShowResendDialog(true);
        setShowModal(false);
        return; // Don't throw - let dialog handle it
      }

      // EXTERNAL_ID_CONFLICT falls through intentionally — the generic error display in UserModal is sufficient
      throw new Error(apiErrorMessage(result, `Failed to ${isCreate ? 'create' : 'update'} user`));
    }

    if (!result.success) {
      throw new Error(apiErrorMessage(result, `Failed to ${isCreate ? 'create' : 'update'} user`));
    }

    // Show success message for new user invitations
    if (isCreate) {
      // Invitation sent - no temporary password, user will set their own via email
      alert(`Invitation sent to ${result.data.email}!\n\nThe user will receive an email with a link to set their password.`);
    }

    // Reload users list and invitations (a new invitation was created)
    await fetchUsers();
    if (isCreate && activeTab === 'invitations') fetchInvitations();
  };

  // Handler for restoring a deleted user
  const handleRestoreUser = async () => {
    if (!restoreUserInfo) return;

    const token = localStorage.getItem('accessToken');
    if (!token) {
      throw new Error('Authentication required');
    }

    setRestoreLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/v1/platform/users/${restoreUserInfo.userId}/restore`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to restore user');
      }

      // Success - reload users
      await fetchUsers();
      setShowRestoreDialog(false);
      setRestoreUserInfo(null);
      alert(`User ${result.data.email} has been restored!\n\nA password reset email has been sent.`);
    } catch (err) {
      console.error('Error restoring user:', err);
      setError(err instanceof Error ? err.message : 'Failed to restore user');
      throw err;
    } finally {
      setRestoreLoading(false);
    }
  };

  // Handler for resending/reviving an invitation from the dialog
  const handleResendFromDialog = async () => {
    if (!resendInvitationInfo) return;

    const token = localStorage.getItem('accessToken');
    if (!token) {
      throw new Error('Authentication required');
    }

    setResendLoading(true);
    try {
      const action = invitationRenewAction(resendInvitationInfo.isExpired, user?.role);
      const endpoint = `${API_URL}/api/v1/platform/invitations/${resendInvitationInfo.invitationId}/${action}`;

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to resend invitation');
      }

      // Success - reload invitations
      if (activeTab === 'invitations') await fetchInvitations();
      setShowResendDialog(false);
      setResendInvitationInfo(null);
      alert(`Invitation ${resendInvitationInfo.isExpired ? 'revived and ' : ''}sent to ${resendInvitationInfo.email}!`);
    } catch (err) {
      console.error('Error resending invitation:', err);
      setError(err instanceof Error ? err.message : 'Failed to resend invitation');
      throw err;
    } finally {
      setResendLoading(false);
    }
  };

  // Handler for withdrawing the existing invitation from the dialog, so the email can be re-invited
  const handleCancelFromDialog = async () => {
    if (!resendInvitationInfo) return;

    const token = localStorage.getItem('accessToken');
    if (!token) {
      throw new Error('Authentication required');
    }

    setResendLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/v1/platform/invitations/${resendInvitationInfo.invitationId}/cancel`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      const result = await response.json().catch(() => ({}));

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to cancel invitation');
      }

      if (activeTab === 'invitations') await fetchInvitations();
      setShowResendDialog(false);
      setResendInvitationInfo(null);
      toast.success(`Invitation to ${resendInvitationInfo.email} cancelled. You can invite this email again.`);
    } catch (err) {
      console.error('Error cancelling invitation:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to cancel invitation');
      throw err;
    } finally {
      setResendLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!deletingUser) return;

    const token = localStorage.getItem('accessToken');
    if (!token) return;

    try {
      setError(null);

      const response = await fetch(`${API_URL}/api/v1/platform/users/${deletingUser.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to delete user: ${response.status}`);
      }

      const result = await response.json();
      if (result.success) {
        setShowDeleteConfirm(false);
        setDeletingUser(null);
        fetchUsers();
      } else {
        throw new Error(result.error || 'Failed to delete user');
      }
    } catch (err) {
      console.error('Error deleting user:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete user');
    }
  };

  const handleReactivateUser = async () => {
    if (!reactivatingUser) return;

    const token = localStorage.getItem('accessToken');
    if (!token) return;

    try {
      setError(null);

      const response = await fetch(`${API_URL}/api/v1/platform/users/${reactivatingUser.id}/reactivate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to reactivate user: ${response.status}`);
      }

      const result = await response.json();
      if (result.success) {
        setShowReactivateConfirm(false);
        setReactivatingUser(null);
        fetchUsers();
      } else {
        throw new Error(result.error || 'Failed to reactivate user');
      }
    } catch (err) {
      console.error('Error reactivating user:', err);
      setError(err instanceof Error ? err.message : 'Failed to reactivate user');
    }
  };

  const deactivateUrl = (userId: string) =>
    `${API_URL}/api/v1/platform/users/${userId}/deactivate`;

  const authHeaders = (token: string) => ({
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  });

  // The first request only asks for confirmation: it answers 200 with `success: true` while
  // changing nothing, so neither flag means the user was deactivated. A refusal carries no
  // `requiresConfirmation` either, so without the `ok` check the control appears to do nothing.
  const initiateDeactivateUser = async (userId: string, email: string) => {
    const token = localStorage.getItem('accessToken');
    if (!token) return;

    try {
      setError(null);

      const response = await fetch(deactivateUrl(userId), {
        method: 'POST',
        headers: authHeaders(token),
        body: JSON.stringify({})
      });
      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || `Failed to deactivate user: ${response.status}`);
      }
      if (result.requiresConfirmation) {
        setDeactivateTarget({ id: userId, email: result.entityName || email });
        return;
      }
      if (result.success) {
        fetchUsers();
        return;
      }
      throw new Error(result.error || 'Failed to deactivate user');
    } catch (err) {
      console.error('Error deactivating user:', err);
      setError(err instanceof Error ? err.message : 'Failed to deactivate user');
    }
  };

  const confirmDeactivateUser = async (confirmEmail: string) => {
    const target = deactivateTarget;
    if (!target) return;

    setIsDeactivating(true);
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) throw new Error('Authentication required');

      const response = await fetch(deactivateUrl(target.id), {
        method: 'POST',
        headers: authHeaders(token),
        body: JSON.stringify({ confirmEmail })
      });
      const result = await response.json();

      // A second confirmation prompt means nothing was deactivated, so it cannot be reported as
      // done even though the request itself succeeded.
      if (!response.ok || !result.success || result.requiresConfirmation) {
        throw new Error(result.error || `Failed to deactivate user: ${response.status}`);
      }

      setDeactivateTarget(null);
      fetchUsers();
    } catch (err) {
      console.error('Error deactivating user:', err);
      // A toast, because the modal stays open over the page's own error banner.
      toast.error(err instanceof Error ? err.message : 'Failed to deactivate user');
    } finally {
      setIsDeactivating(false);
    }
  };

  // Invitation handlers
  useEffect(() => () => {
    if (invitationSentTimer.current) clearTimeout(invitationSentTimer.current);
  }, []);

  const invitationSendState = (invitationId: string): InvitationSendState => {
    if (invitationInFlight === invitationId) return 'sending';
    if (invitationJustSent === invitationId) return 'sent';
    return 'idle';
  };

  const confirmInvitationSent = (invitationId: string) => {
    if (invitationSentTimer.current) clearTimeout(invitationSentTimer.current);
    setInvitationJustSent(invitationId);
    invitationSentTimer.current = setTimeout(() => setInvitationJustSent(null), 1800);
  };

  const handleResendInvitation = async (invitationId: string) => {
    const token = localStorage.getItem('accessToken');
    if (!token) return;
    if (invitationInFlightRef.current) return;
    invitationInFlightRef.current = invitationId;

    try {
      setError(null);
      setInvitationInFlight(invitationId);

      const response = await fetch(`${API_URL}/api/v1/platform/invitations/${invitationId}/resend`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || `Failed to resend invitation: ${response.status}`);
      }

      if (result.success) {
        toast.success('Invitation resent successfully!');
        confirmInvitationSent(invitationId);
        fetchInvitations();
      } else {
        throw new Error(result.error || 'Failed to resend invitation');
      }
    } catch (err) {
      console.error('Error resending invitation:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to resend invitation');
    } finally {
      invitationInFlightRef.current = null;
      setInvitationInFlight(null);
    }
  };

  const handleCancelInvitation = async (invitationId: string) => {
    if (!window.confirm('Are you sure you want to cancel this invitation? The user will not be able to accept it.')) {
      return;
    }

    const token = localStorage.getItem('accessToken');
    if (!token) return;

    try {
      setError(null);

      const response = await fetch(`${API_URL}/api/v1/platform/invitations/${invitationId}/cancel`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || `Failed to cancel invitation: ${response.status}`);
      }

      if (result.success) {
        toast.success('Invitation cancelled.');
        fetchInvitations();
      } else {
        throw new Error(result.error || 'Failed to cancel invitation');
      }
    } catch (err) {
      console.error('Error cancelling invitation:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to cancel invitation');
    }
  };

  // Revive expired invitation handler
  const handleReviveInvitation = async (invitationId: string) => {
    const token = localStorage.getItem('accessToken');
    if (!token) return;
    if (invitationInFlightRef.current) return;
    invitationInFlightRef.current = invitationId;

    try {
      setError(null);
      setInvitationInFlight(invitationId);

      const response = await fetch(`${API_URL}/api/v1/platform/invitations/${invitationId}/revive`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || result.error || `Failed to revive invitation: ${response.status}`);
      }

      if (result.success) {
        toast.success('Invitation revived! A new invitation email has been sent.');
        confirmInvitationSent(invitationId);
        fetchInvitations();
      } else {
        throw new Error(result.message || result.error || 'Failed to revive invitation');
      }
    } catch (err) {
      console.error('Error reviving invitation:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to revive invitation');
    } finally {
      invitationInFlightRef.current = null;
      setInvitationInFlight(null);
    }
  };

  const handleEditInvitation = async (inv: InvitationRecord) => {
    try {
      const { getInvitation } = await import('../services/invitations.service');
      const { invitation } = await getInvitation(inv.id);
      // The backend returns the full entity — includes nested `metadata` and top-level enterpriseId/partnerId
      setSelectedInvitation(invitation as EditableInvitation);
      setEditingUser(null);
      setModalMode('edit-invitation');
      setShowModal(true);
    } catch (error) {
      console.error('Failed to fetch invitation for edit:', error);
      toast.error('Failed to load invitation details. Please try again.');
    }
  };

  // --- Invitation tab helpers ---
  const handleInvitationFilterChange = (key: keyof typeof invitationFilters, value: string) => {
    setInvitationFilters(prev => ({ ...prev, [key]: value }));
    setInvitationPagination(prev => ({ ...prev, page: 1 }));
    setSelectedInvitationIds(new Set());
  };

  const handleSelectInvitation = (id: string) => {
    setSelectedInvitationIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSelectAllInvitations = () => {
    if (selectedInvitationIds.size === invitationRecords.length) {
      setSelectedInvitationIds(new Set());
    } else {
      setSelectedInvitationIds(new Set(invitationRecords.map(i => i.id)));
    }
  };

  const handleBulkResendInvitations = async () => {
    const ids = Array.from(selectedInvitationIds);
    if (ids.length === 0) return;

    const token = localStorage.getItem('accessToken');
    if (!token) return;

    try {
      const response = await fetch(`${API_URL}/api/v1/platform/invitations/bulk-resend`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ invitationIds: ids })
      });
      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Bulk resend failed');
      }
      const failed = (result.results || []).filter((r: { status: string }) => r.status === 'failed');
      if (failed.length > 0) {
        toast.error(`Sent: ${ids.length - failed.length}, Failed: ${failed.length}`);
      } else {
        toast.success(`Successfully resent ${ids.length} invitation${ids.length > 1 ? 's' : ''}`);
      }
      setSelectedInvitationIds(new Set());
      fetchInvitations();
    } catch (err) {
      console.error('Bulk resend error:', err);
      toast.error(err instanceof Error ? err.message : 'Bulk resend failed');
    }
  };

  // Cancel on an Invitations-tab row: unaccepted, inside the caller's tier, and not refused by the
  // list's sender check.
  const canCancelRecord = (inv: InvitationRecord) =>
    (inv.status === 'pending' || inv.status === 'expired') &&
    inv.canManage !== false &&
    canCancelInvitation({ role: user?.role, hasPermission }, inv.role);

  const handleBulkCancelInvitations = async () => {
    const selected = Array.from(selectedInvitationIds);
    // Only the rows this caller may cancel are sent; the rest are reported as skipped.
    const ids = selected.filter(id => {
      const inv = invitationRecords.find(i => i.id === id);
      return !!inv && canCancelRecord(inv);
    });
    const skipped = selected.length - ids.length;
    if (ids.length === 0) return;
    const skipNote = skipped > 0 ? ` ${skipped} you can't cancel will be skipped.` : '';
    if (!window.confirm(`Cancel ${ids.length} invitation${ids.length > 1 ? 's' : ''}?${skipNote}`)) return;

    const token = localStorage.getItem('accessToken');
    if (!token) return;

    setLoadingInvitations(true);
    const BATCH_SIZE = 5;
    let succeeded = 0;
    let failed = 0;

    for (let i = 0; i < ids.length; i += BATCH_SIZE) {
      const batch = ids.slice(i, i + BATCH_SIZE);
      const results = await Promise.allSettled(
        batch.map(id =>
          fetch(`${API_URL}/api/v1/platform/invitations/${id}/cancel`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
          })
        )
      );
      // fetch resolves on any HTTP status, so a refused cancel is a fulfilled promise too.
      const ok = results.filter(r => r.status === 'fulfilled' && r.value.ok).length;
      succeeded += ok;
      failed += results.length - ok;
    }

    if (failed > 0) {
      toast.error(`Cancelled: ${succeeded}, Failed: ${failed}${skipped > 0 ? `, Skipped: ${skipped}` : ''}`);
    } else {
      toast.success(`Cancelled ${succeeded} invitation${succeeded > 1 ? 's' : ''}${skipped > 0 ? `, skipped ${skipped}` : ''}`);
    }
    setSelectedInvitationIds(new Set());
    setLoadingInvitations(false);
    fetchInvitations();
  };

  // Password reset handler for users
  const handleTriggerPasswordReset = async (userId: string, userEmail: string) => {
    if (!confirm(`Send password reset email to ${userEmail}?`)) {
      return;
    }

    const token = localStorage.getItem('accessToken');
    if (!token) return;

    try {
      setError(null);

      const response = await fetch(`${API_URL}/api/v1/platform/users/${userId}/password-reset`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || result.error || `Failed to trigger password reset: ${response.status}`);
      }

      if (result.success) {
        alert(result.message || 'Password reset email sent successfully!');
      } else {
        throw new Error(result.message || result.error || 'Failed to trigger password reset');
      }
    } catch (err) {
      console.error('Error triggering password reset:', err);
      setError(err instanceof Error ? err.message : 'Failed to trigger password reset');
    }
  };

  // Bulk selection handlers
  const handleSelectUser = (userId: string) => {
    const newSelection = new Set(selectedUserIds);
    if (newSelection.has(userId)) {
      newSelection.delete(userId);
    } else {
      if (newSelection.size >= 100) {
        setError('Maximum 100 users can be selected at once');
        return;
      }
      newSelection.add(userId);
    }
    setSelectedUserIds(newSelection);
  };

  const handleSelectAll = () => {
    if (selectedUserIds.size === users.length) {
      setSelectedUserIds(new Set());
    } else {
      const selectableUsers = users.slice(0, 100);
      setSelectedUserIds(new Set(selectableUsers.map(u => u.id!)));
      if (users.length > 100) {
        setError('Only first 100 users selected (maximum limit)');
      }
    }
  };

  const handleClearSelection = () => {
    setSelectedUserIds(new Set());
  };

  // Bulk operations
  const handleBulkActivate = async (userIds: string[]) => {
    const token = localStorage.getItem('accessToken');
    if (!token) return;

    let successCount = 0;
    const errors: string[] = [];

    for (const userId of userIds) {
      try {
        const response = await fetch(`${API_URL}/api/v1/platform/users/${userId}/reactivate`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          successCount++;
        } else {
          const result = await response.json();
          errors.push(`User ${userId}: ${result.error || 'Failed'}`);
        }
      } catch (err) {
        errors.push(`User ${userId}: Network error`);
      }
    }

    // Show results
    if (errors.length > 0) {
      setError(`Activated ${successCount}/${userIds.length} users. Errors: ${errors.join(', ')}`);
    } else {
      setError(null);
      alert(`Successfully activated ${successCount} user${successCount > 1 ? 's' : ''}`);
    }

    setSelectedUserIds(new Set());
    fetchUsers();
  };

  const handleBulkDeactivate = async (userIds: string[]) => {
    const token = localStorage.getItem('accessToken');
    if (!token) return;

    // Filter out active users only
    const activeUserIds = userIds.filter(id => {
      const user = users.find(u => u.id === id);
      return user?.isActive;
    });

    if (activeUserIds.length === 0) {
      setError('No active users selected to deactivate');
      return;
    }

    let successCount = 0;
    const errors: string[] = [];

    for (const userId of activeUserIds) {
      try {
        // Deactivate by updating user with isActive: false
        const response = await fetch(`${API_URL}/api/v1/platform/users/${userId}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ isActive: false })
        });

        if (response.ok) {
          successCount++;
        } else {
          const result = await response.json();
          errors.push(`User ${userId}: ${result.error || 'Failed'}`);
        }
      } catch (err) {
        errors.push(`User ${userId}: Network error`);
      }
    }

    if (errors.length > 0) {
      setError(`Deactivated ${successCount}/${activeUserIds.length} users. Errors: ${errors.join(', ')}`);
    } else {
      setError(null);
      alert(`Successfully deactivated ${successCount} user${successCount > 1 ? 's' : ''}`);
    }

    setSelectedUserIds(new Set());
    fetchUsers();
  };

  const handleBulkDelete = async (userIds: string[]) => {
    if (!confirm(`Are you sure you want to delete ${userIds.length} user${userIds.length > 1 ? 's' : ''}? This will soft-delete them.`)) {
      return;
    }

    const token = localStorage.getItem('accessToken');
    if (!token) return;

    let successCount = 0;
    const errors: string[] = [];

    for (const userId of userIds) {
      try {
        const response = await fetch(`${API_URL}/api/v1/platform/users/${userId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          successCount++;
        } else {
          const result = await response.json();
          errors.push(`User ${userId}: ${result.error || 'Failed'}`);
        }
      } catch (err) {
        errors.push(`User ${userId}: Network error`);
      }
    }

    if (errors.length > 0) {
      setError(`Deleted ${successCount}/${userIds.length} users. Errors: ${errors.join(', ')}`);
    } else {
      setError(null);
      alert(`Successfully deleted ${successCount} user${successCount > 1 ? 's' : ''}`);
    }

    setSelectedUserIds(new Set());
    fetchUsers();
  };

  const handleBulkRoleChange = async (userIds: string[], newRole: string) => {
    if (!confirm(`Change role to "${getRoleDisplayName(newRole)}" for ${userIds.length} user${userIds.length > 1 ? 's' : ''}?`)) {
      return;
    }

    const token = localStorage.getItem('accessToken');
    if (!token) return;

    let successCount = 0;
    const errors: string[] = [];

    for (const userId of userIds) {
      try {
        const user = users.find(u => u.id === userId);
        if (!user) continue;

        const response = await fetch(`${API_URL}/api/v1/platform/users/${userId}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ ...user, role: newRole })
        });

        if (response.ok) {
          successCount++;
        } else {
          // A role refusal names its reason in `details`, not in `error`, and per-user reasons are
          // the only way to act on a partly-applied bulk change.
          const result = await response.json();
          errors.push(`${user.firstName} ${user.lastName}: ${apiErrorMessage(result, 'Failed')}`);
        }
      } catch (err) {
        errors.push(`User ${userId}: Network error`);
      }
    }

    if (errors.length > 0) {
      setError(`Updated ${successCount}/${userIds.length} users. Errors: ${errors.join(', ')}`);
    } else {
      setError(null);
      alert(`Successfully changed role for ${successCount} user${successCount > 1 ? 's' : ''}`);
    }

    setSelectedUserIds(new Set());
    fetchUsers();
  };

  // DataTable column definitions
  const userColumns: ColumnDefinition<UserOrInvitation>[] = useMemo(() => [
    {
      id: 'user',
      header: 'User',
      sortable: true,
      width: '100%',
      cellClassName: 'max-w-0',
      accessor: (item) => (
        <div className="flex items-center min-w-0">
          <GradientAvatar user={item as unknown as UserData} size="md" />
          <div className="ml-4 min-w-0">
            <div className="text-sm font-medium text-gray-900 flex items-center gap-2 min-w-0">
              <span className="truncate">{item.firstName} {item.lastName}</span>
              {item.type === 'invitation' && (
                <span className="inline-flex items-center shrink-0 px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                  <EnvelopeIcon className="h-3 w-3 mr-1" />
                  Invitation
                </span>
              )}
            </div>
            <div className="text-sm text-gray-500 flex items-center min-w-0" title={item.email}>
              <EnvelopeIcon className="h-3 w-3 mr-1 shrink-0" />
              <span className="truncate">{item.email}</span>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'role',
      header: 'Role',
      sortable: true,
      accessor: (item) => {
        const roles = getUserDisplayRoles(item as unknown as UserData);
        return roles.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {roles.map((role, idx) => (
              <span key={idx} className="inline-flex items-center">
                {role.name === 'superadmin' && <ShieldCheckIcon className="h-3 w-3 mr-1" />}
                <RoleBadge role={role} />
              </span>
            ))}
          </div>
        ) : (
          <span className="text-sm text-gray-400 italic">No role</span>
        );
      },
    },
    {
      id: 'organization',
      header: 'Organization',
      sortable: true,
      accessor: (item) => (
        <div className="flex items-center text-sm text-gray-900 min-w-0 max-w-[180px]" title={item.organizationName || undefined}>
          {item.role === 'superadmin' ? (
            <span className="inline-flex items-center shrink-0 px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
              Platform
            </span>
          ) : (
            <>
              <BuildingOfficeIcon className="h-4 w-4 mr-1 text-gray-400 shrink-0" />
              <span className="truncate">{item.organizationName || '-'}</span>
            </>
          )}
        </div>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      sortable: true,
      accessor: (item) => {
        if (item.type === 'invitation') {
          const isExpired = item.expiresAt && new Date(item.expiresAt) < new Date();
          if (isExpired) {
            return (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                Invitation Expired
              </span>
            );
          }
          return (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              Invitation Pending
            </span>
          );
        }
        return (
          <StatusBadge
            status={item.isDeleted ? 'deleted' : item.isActive ? 'active' : 'inactive'}
          />
        );
      },
    },
    {
      id: 'created',
      header: 'Created',
      sortable: true,
      width: '120px',
      accessor: (item) => (
        <span className="text-sm text-gray-500">
          {item.createdAt ? new Date(item.createdAt).toLocaleDateString() : '-'}
        </span>
      ),
    },
  ], []);

  // DataTable action definitions
  const userActions: ActionDefinition<UserOrInvitation>[] = useMemo(() => [
    // User actions
    {
      id: 'edit',
      icon: <PencilIcon className="h-4 w-4" />,
      tooltip: 'Edit user',
      onClick: (item) => handleEditUser(item as User),
      show: (item) => item.type === 'user' && !item.isDeleted,
      variant: 'primary',
    },
    {
      id: 'audit',
      icon: <ClockIcon className="h-4 w-4" />,
      tooltip: 'View audit history',
      onClick: (item) => {
        setAuditUser(item as User);
        setShowAuditHistory(true);
      },
      show: (item) => item.type === 'user' && !item.isDeleted,
      variant: 'default',
    },
    {
      id: 'delete',
      icon: <TrashIcon className="h-4 w-4" />,
      tooltip: 'Delete user',
      onClick: (item) => setDeletingUser(item as User),
      show: (item) => isSuperAdmin() && item.type === 'user' && !item.isDeleted && item.role !== 'superadmin',
      variant: 'danger',
    },
    {
      id: 'deactivate',
      icon: <XCircleIcon className="h-4 w-4" />,
      tooltip: 'Deactivate superadmin',
      onClick: (item) => {
        initiateDeactivateUser(item.id!, item.email || '');
      },
      show: (item) => item.type === 'user' && !item.isDeleted && item.role === 'superadmin' && item.isActive === true,
      variant: 'danger',
    },
    {
      id: 'reactivate',
      icon: <ArrowPathIcon className="h-4 w-4" />,
      tooltip: 'Reactivate user',
      onClick: (item) => {
        setReactivatingUser(item as User);
        setShowReactivateConfirm(true);
      },
      show: (item) => item.type === 'user' && (item.isDeleted === true || (item.role === 'superadmin' && item.isActive === false)),
      variant: 'success',
    },
    {
      id: 'password-reset',
      icon: <KeyIcon className="h-4 w-4" />,
      tooltip: 'Send password reset email',
      onClick: (item) => handleTriggerPasswordReset(item.id!, item.email),
      show: (item) => item.type === 'user' && !item.isDeleted && item.isActive === true,
      variant: 'default',
    },
    // Invitation actions
    {
      id: 'resend',
      icon: <PaperAirplaneIcon className="h-4 w-4" />,
      tooltip: 'Resend invitation (max 2/day)',
      onClick: (item) => handleResendInvitation(item.invitationId!),
      // Resend follows the endpoint's own rule, which is wider than superadmin: the caller may
      // nudge an invitation it could not have created, but never one above its own role tier.
      show: (item) =>
        item.type === 'invitation' &&
        item.invitationStatus === 'pending' &&
        canResendInvitation({ role: user?.role, hasPermission }, item.role),
      variant: 'primary',
    },
    {
      id: 'revive',
      icon: <ArrowPathIcon className="h-4 w-4" />,
      tooltip: 'Revive expired invitation',
      onClick: (item) => handleReviveInvitation(item.invitationId!),
      show: (item) => isSuperAdmin() && item.type === 'invitation' && item.invitationStatus === 'expired' ? true : false,
      variant: 'success',
    },
    {
      id: 'cancel-invitation',
      icon: <XCircleIcon className="h-4 w-4" />,
      tooltip: 'Cancel invitation',
      onClick: (item) => handleCancelInvitation(item.invitationId!),
      // Same guard as resend on the server. Expired invitations are included: one that was never
      // accepted still blocks a fresh invite to the same email.
      show: (item) =>
        item.type === 'invitation' &&
        (item.invitationStatus === 'pending' || item.invitationStatus === 'expired') &&
        canCancelInvitation({ role: user?.role, hasPermission }, item.role),
      variant: 'danger',
    },
  ], [isSuperAdmin, user?.role, hasPermission, initiateDeactivateUser, handleResendInvitation, handleCancelInvitation, handleReviveInvitation, handleTriggerPasswordReset]);

  // Handler for selection change from DataTable
  const handleSelectionChange = useCallback((newSelectedIds: Set<string>) => {
    if (newSelectedIds.size > 100) {
      setError('Maximum 100 users can be selected at once');
      return;
    }
    setSelectedUserIds(newSelectedIds);
  }, []);

  // Full-page spinner only on initial load (no data yet)
  if (loading && users.length === 0 && !debouncedSearch && !error) {
    return (
      <PageTransition>
        <div className="flex items-center justify-center h-64">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          >
            <div className="h-12 w-12 border-4 border-purple-600 border-t-transparent rounded-full" />
          </motion.div>
        </div>
      </PageTransition>
    );
  }

  return (
    <PageTransition className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Platform Users</h1>
          <div className="mt-1 flex items-center gap-4">
            <p className="text-sm text-gray-600">
              Manage users across all organizations
            </p>
            {activeTab === 'users' && (
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <kbd className="px-2 py-1 bg-gray-100 border border-gray-300 rounded">Ctrl+A</kbd>
                <span>Select all</span>
                <span className="mx-1">•</span>
                <kbd className="px-2 py-1 bg-gray-100 border border-gray-300 rounded">Esc</kbd>
                <span>Clear/Close</span>
              </div>
            )}
          </div>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setShowDeleted(!showDeleted)}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            {showDeleted ? 'Hide Deleted' : 'Show Deleted'}
          </button>
          <UserExport
            users={users}
            totalUsers={pagination.total}
            filterActive={filterRole !== 'all' || filterOrganization !== 'all' || filterEnterprise !== 'all' || filterPartner !== 'all' || filterStatus !== 'all' || filterActivity !== 'all' || filterCredentials !== 'all' || filterNoExternalId || filterExpiredCredentials || searchQuery.trim() !== ''}
          />
          {canInviteUser && (
            <button
              onClick={handleCreateClick}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              Invite User
            </button>
          )}
        </div>
      </div>

      {/* Tab navigation */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-2">
          <TabsTrigger value="users">
            <UserGroupIcon className="h-4 w-4 mr-2" />
            Users
          </TabsTrigger>
          <TabsTrigger value="invitations">
            <EnvelopeIcon className="h-4 w-4 mr-2" />
            Invitations
          </TabsTrigger>
        </TabsList>

        {/* ── Users tab ── */}
        <TabsContent value="users">

      {/* Filters and Search */}
      <div className="bg-white shadow rounded-lg p-4 space-y-4">
        {/* First Row: Search and Primary Filters */}
        <div className="flex items-center space-x-4">
          {/* Search Input */}
          <div className="flex-1 max-w-md">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by name, email, specialty, department..."
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
              />
            </div>
          </div>

          {/* Role Filter */}
          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium text-gray-700">Role:</label>
            <select
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value)}
              className="rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 text-sm"
            >
              <option value="all">All Roles</option>
              {/* Narrowed to the roles this caller's forced scope can return. */}
              {roleFilterOptions.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>

          {/* Organization Filter */}
          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium text-gray-700">Organization:</label>
            <select
              value={filterRole === 'superadmin' ? 'all' : filterOrganization}
              onChange={(e) => setFilterOrganization(e.target.value)}
              className="rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={filterRole === 'superadmin'}
              title={filterRole === 'superadmin' ? 'Superadmins are not assigned to organizations' : undefined}
            >
              <option value="all">All Organizations</option>
              {filteredOrganizations.map(org => (
                <option key={org.id} value={org.id}>{org.name}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Second Row: Hierarchy Filters */}
        <div className="flex items-center space-x-4">
          {/* Enterprise Filter */}
          {enterprises.length > 0 && (
            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium text-gray-700">Enterprise:</label>
              <select
                value={filterRole === 'superadmin' ? 'all' : filterEnterprise}
                onChange={(e) => setFilterEnterprise(e.target.value)}
                className="rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={filterRole === 'superadmin'}
                title={filterRole === 'superadmin' ? 'Superadmins are not assigned to enterprises' : undefined}
              >
                <option value="all">All Enterprises</option>
                {enterprises.map(ent => (
                  <option key={ent.id} value={ent.id}>{ent.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Channel Partner Filter */}
          {partners.length > 0 && (
            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium text-gray-700">Channel Partner:</label>
              <select
                value={filterRole === 'superadmin' ? 'all' : filterPartner}
                onChange={(e) => setFilterPartner(e.target.value)}
                className="rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={filterRole === 'superadmin'}
                title={filterRole === 'superadmin' ? 'Superadmins are not assigned to partners' : undefined}
              >
                <option value="all">All Partners</option>
                {filteredPartners.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Third Row: Advanced Filters */}
        <div className="flex items-center space-x-4">
          {/* Status Filter */}
          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium text-gray-700">Status:</label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 text-sm"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          {/* Last Activity Filter */}
          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium text-gray-700">Last Activity:</label>
            <select
              value={filterActivity}
              onChange={(e) => setFilterActivity(e.target.value)}
              className="rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 text-sm"
            >
              <option value="all">Any Time</option>
              <option value="7days">Last 7 Days</option>
              <option value="30days">Last 30 Days</option>
              <option value="90days">Last 90 Days</option>
            </select>
          </div>

          {/* Medical Credentials Filter */}
          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium text-gray-700">Credentials:</label>
            <select
              value={filterCredentials}
              onChange={(e) => setFilterCredentials(e.target.value)}
              className="rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 text-sm"
            >
              <option value="all">All Users</option>
              <option value="hasLicense">Has Medical License</option>
              <option value="noLicense">No Medical License</option>
            </select>
          </div>

          {/* Quick Filters Preset */}
          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium text-gray-700">Quick Filters:</label>
            <select
              value=""
              onChange={(e) => {
                const preset = e.target.value;
                if (preset === 'pending_invitations') {
                  setActiveTab('invitations');
                  setInvitationFilters(prev => ({ ...prev, status: 'pending' }));
                } else if (preset === 'no_external_id') {
                  setFilterNoExternalId(true);
                } else if (preset === 'expired_credentials') {
                  setFilterExpiredCredentials(true);
                } else if (preset === 'inactive_90days') {
                  setFilterActivity('90days');
                }
              }}
              className="rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 text-sm"
            >
              <option value="">Apply preset...</option>
              <option value="pending_invitations">Pending invitations</option>
              <option value="no_external_id">No external ID</option>
              <option value="expired_credentials">Expired credentials</option>
              <option value="inactive_90days">Inactive 90 days</option>
            </select>
          </div>

          {/* Clear Filters Button */}
          {(filterRole !== 'all' || filterOrganization !== 'all' || filterEnterprise !== 'all' || filterPartner !== 'all' || filterStatus !== 'all' ||
            filterActivity !== 'all' || filterCredentials !== 'all' || filterNoExternalId || filterExpiredCredentials || searchQuery.trim()) && (
            <button
              onClick={() => {
                setFilterRole('all');
                setFilterOrganization('all');
                setFilterEnterprise('all');
                setFilterPartner('all');
                setFilterStatus('all');
                setFilterActivity('all');
                setFilterCredentials('all');
                setFilterNoExternalId(false);
                setFilterExpiredCredentials(false);
                setSearchQuery('');
                window.history.replaceState({}, '', window.location.pathname);
              }}
              className="text-sm text-purple-600 hover:text-purple-800 font-medium whitespace-nowrap"
            >
              Clear All Filters
            </button>
          )}
        </div>

        {/* Filter Pills */}
        {(filterRole !== 'all' || filterOrganization !== 'all' || filterEnterprise !== 'all' || filterPartner !== 'all' || filterStatus !== 'all' ||
          filterActivity !== 'all' || filterCredentials !== 'all' || filterNoExternalId || filterExpiredCredentials) && (
          <div className="flex items-center gap-2 flex-wrap pt-2 border-t border-gray-200">
            <span className="text-xs text-gray-500 font-medium">Active Filters:</span>
            {filterRole !== 'all' && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                Role: {getRoleDisplayName(filterRole)}
                <button
                  onClick={() => setFilterRole('all')}
                  className="ml-1 text-purple-600 hover:text-purple-800"
                >
                  ×
                </button>
              </span>
            )}
            {filterOrganization !== 'all' && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-pink-100 text-pink-800">
                Org: {organizations.find(o => o.id === filterOrganization)?.name || filterOrganization}
                <button
                  onClick={() => setFilterOrganization('all')}
                  className="ml-1 text-pink-600 hover:text-pink-800"
                >
                  ×
                </button>
              </span>
            )}
            {filterEnterprise !== 'all' && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                Enterprise: {enterprises.find(e => e.id === filterEnterprise)?.name || filterEnterprise}
                <button
                  onClick={() => setFilterEnterprise('all')}
                  className="ml-1 text-indigo-600 hover:text-indigo-800"
                >
                  ×
                </button>
              </span>
            )}
            {filterPartner !== 'all' && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-teal-100 text-teal-800">
                Partner: {partners.find(p => p.id === filterPartner)?.name || filterPartner}
                <button
                  onClick={() => setFilterPartner('all')}
                  className="ml-1 text-teal-600 hover:text-teal-800"
                >
                  ×
                </button>
              </span>
            )}
            {filterStatus !== 'all' && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                Status: {filterStatus.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                <button
                  onClick={() => setFilterStatus('all')}
                  className="ml-1 text-blue-600 hover:text-blue-800"
                >
                  ×
                </button>
              </span>
            )}
            {filterActivity !== 'all' && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                Activity: {filterActivity}
                <button
                  onClick={() => setFilterActivity('all')}
                  className="ml-1 text-purple-600 hover:text-purple-800"
                >
                  ×
                </button>
              </span>
            )}
            {filterCredentials !== 'all' && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-pink-100 text-pink-800">
                Credentials: {filterCredentials === 'hasLicense' ? 'Has License' : 'No License'}
                <button
                  onClick={() => setFilterCredentials('all')}
                  className="ml-1 text-pink-600 hover:text-pink-800"
                >
                  ×
                </button>
              </span>
            )}
            {filterNoExternalId && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                No External ID
                <button
                  onClick={() => setFilterNoExternalId(false)}
                  className="ml-1 text-orange-600 hover:text-orange-800"
                >
                  ×
                </button>
              </span>
            )}
            {filterExpiredCredentials && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                Expired Credentials
                <button
                  onClick={() => setFilterExpiredCredentials(false)}
                  className="ml-1 text-red-600 hover:text-red-800"
                >
                  ×
                </button>
              </span>
            )}
          </div>
        )}

        {/* Results Count - now handled by pagination component */}
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Bulk Actions Bar */}
      {selectedUserIds.size > 0 && (
        <BulkActionsBar
          selectedUserIds={selectedUserIds}
          users={users}
          onClearSelection={handleClearSelection}
          onBulkActivate={handleBulkActivate}
          onBulkDeactivate={handleBulkDeactivate}
          onBulkDelete={handleBulkDelete}
          onBulkRoleChange={handleBulkRoleChange}
        />
      )}

      {/* Inline loading indicator during refetch */}
      {loading && users.length > 0 && (
        <div className="flex items-center justify-center py-2">
          <div className="h-5 w-5 border-2 border-purple-600 border-t-transparent rounded-full animate-spin" />
          <span className="ml-2 text-sm text-gray-500">Updating...</span>
        </div>
      )}

      {/* Users Table or Empty State */}
      {users.length === 0 && !loading ? (
        <EmptyState
          icon={<UserGroupIcon className="h-16 w-16 text-gray-400" />}
          title={
            searchQuery || filterRole !== 'all' || filterOrganization !== 'all' || filterEnterprise !== 'all' || filterPartner !== 'all' || filterStatus !== 'all' || filterActivity !== 'all' || filterCredentials !== 'all' || filterNoExternalId || filterExpiredCredentials
              ? "No users match your filters"
              : showDeleted
                ? "No deleted users found"
                : "No users yet"
          }
          description={
            searchQuery || filterRole !== 'all' || filterOrganization !== 'all' || filterEnterprise !== 'all' || filterPartner !== 'all' || filterStatus !== 'all' || filterActivity !== 'all' || filterCredentials !== 'all' || filterNoExternalId || filterExpiredCredentials
              ? "Try adjusting your search or filters to find what you're looking for."
              : showDeleted
                ? "There are no deleted users in the system."
                : "Get started by creating your first user account."
          }
          actionLabel={
            searchQuery || filterRole !== 'all' || filterOrganization !== 'all' || filterEnterprise !== 'all' || filterPartner !== 'all' || filterStatus !== 'all' || filterActivity !== 'all' || filterCredentials !== 'all' || filterNoExternalId || filterExpiredCredentials
              ? "Clear All Filters"
              : !showDeleted
                ? "Create User"
                : undefined
          }
          onAction={
            searchQuery || filterRole !== 'all' || filterOrganization !== 'all' || filterEnterprise !== 'all' || filterPartner !== 'all' || filterStatus !== 'all' || filterActivity !== 'all' || filterCredentials !== 'all' || filterNoExternalId || filterExpiredCredentials
              ? () => {
                  setSearchQuery('');
                  setFilterRole('all');
                  setFilterOrganization('all');
                  setFilterEnterprise('all');
                  setFilterPartner('all');
                  setFilterStatus('all');
                  setFilterActivity('all');
                  setFilterCredentials('all');
                  setFilterNoExternalId(false);
                  setFilterExpiredCredentials(false);
                }
              : !showDeleted
                ? () => {
                    setModalMode('create');
                    setEditingUser(null);
                    setShowModal(true);
                  }
                : undefined
          }
        />
      ) : (
        <DataTable
          data={users}
          columns={userColumns}
          actions={userActions}
          selectable={true}
          selectedIds={selectedUserIds}
          onSelectionChange={handleSelectionChange}
          maxSelection={100}
          sortable={true}
          sortState={sortState}
          onSortChange={setSortState}
          striped={true}
          hoverable={true}
          stickyHeader={true}
          maxHeight="calc(100vh - 24rem)"
          className="shadow rounded-lg"
          getRowId={(user) => user.id || ''}
          ariaLabel="Platform users table"
          pagination={pagination}
          onPageChange={handlePageChange}
          onLimitChange={handleLimitChange}
        />
      )}

        </TabsContent>

        {/* ── Invitations tab ── */}
        <TabsContent value="invitations">
          <div className="space-y-4">
            {/* Invitations filter bar */}
            <div className="bg-white shadow rounded-lg p-4">
              <div className="flex flex-wrap items-center gap-4">
                {/* Search */}
                <div className="flex-1 min-w-48 max-w-xs">
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      value={invitationFilters.search}
                      onChange={(e) => handleInvitationFilterChange('search', e.target.value)}
                      placeholder="Search by email..."
                      className="block w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm bg-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
                    />
                  </div>
                </div>

                {/* Status */}
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-gray-700 whitespace-nowrap">Status:</label>
                  <select
                    value={invitationFilters.status}
                    onChange={(e) => handleInvitationFilterChange('status', e.target.value)}
                    className="rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 text-sm"
                  >
                    <option value="all">Pending & Expired</option>
                    <option value="pending">Pending</option>
                    <option value="expired">Expired</option>
                    <option value="accepted">Accepted</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>

                {/* Organization */}
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-gray-700 whitespace-nowrap">Organization:</label>
                  <select
                    value={invitationFilters.organizationId}
                    onChange={(e) => handleInvitationFilterChange('organizationId', e.target.value)}
                    className="rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 text-sm"
                  >
                    <option value="all">All</option>
                    {filteredInvOrganizations.map(org => (
                      <option key={org.id} value={org.id}>{org.name}</option>
                    ))}
                  </select>
                </div>

                {/* Partner */}
                {partners.length > 0 && (
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-medium text-gray-700 whitespace-nowrap">Partner:</label>
                    <select
                      value={invitationFilters.partnerId}
                      onChange={(e) => handleInvitationFilterChange('partnerId', e.target.value)}
                      className="rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 text-sm"
                    >
                      <option value="all">All</option>
                      {partners.map(p => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Role */}
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-gray-700 whitespace-nowrap">Role:</label>
                  <select
                    value={invitationFilters.role}
                    onChange={(e) => handleInvitationFilterChange('role', e.target.value)}
                    className="rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 text-sm"
                  >
                    <option value="all">All</option>
                    {/* Same list as the users tab: an invitation carries the same role enum, and the
                        same scope decides which of them this caller can reach. */}
                    {roleFilterOptions.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>

                {/* Clear */}
                {(invitationFilters.status !== 'all' || invitationFilters.organizationId !== 'all' ||
                  invitationFilters.partnerId !== 'all' || invitationFilters.role !== 'all' || invitationFilters.search) && (
                  <button
                    onClick={() => {
                      setInvitationFilters({ status: 'all', organizationId: 'all', partnerId: 'all', role: 'all', search: '' });
                      setInvitationPagination(prev => ({ ...prev, page: 1 }));
                      setSelectedInvitationIds(new Set());
                    }}
                    className="text-sm text-purple-600 hover:text-purple-800 font-medium whitespace-nowrap"
                  >
                    Clear Filters
                  </button>
                )}
              </div>
            </div>

            {/* Bulk action bar for invitations */}
            {selectedInvitationIds.size > 0 && (
              <div className="bg-purple-50 border border-purple-200 rounded-lg px-4 py-3 flex items-center justify-between">
                <span className="text-sm font-medium text-purple-900">
                  {selectedInvitationIds.size} invitation{selectedInvitationIds.size > 1 ? 's' : ''} selected
                </span>
                <div className="flex items-center gap-3">
                  {isSuperAdmin() && (
                    <button
                      onClick={handleBulkResendInvitations}
                      disabled={!Array.from(selectedInvitationIds).every(id => {
                        const inv = invitationRecords.find(i => i.id === id);
                        return inv && inv.status === 'expired';
                      })}
                      className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-purple-600 rounded-md hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <PaperAirplaneIcon className="h-4 w-4 mr-1" />
                      Resend selected
                    </button>
                  )}
                  {hasPermission('platform.users.manage') && (
                    <button
                      onClick={handleBulkCancelInvitations}
                      // Enabled when any selected row can be cancelled; the rest are skipped.
                      disabled={!Array.from(selectedInvitationIds).some(id => {
                        const inv = invitationRecords.find(i => i.id === id);
                        return !!inv && canCancelRecord(inv);
                      })}
                      className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <XCircleIcon className="h-4 w-4 mr-1" />
                      Cancel selected
                    </button>
                  )}
                  <button
                    onClick={() => setSelectedInvitationIds(new Set())}
                    className="text-sm text-gray-600 hover:text-gray-900"
                  >
                    Clear
                  </button>
                </div>
              </div>
            )}

            {/* Invitations table */}
            {loadingInvitations ? (
              <div className="flex items-center justify-center h-32">
                <div className="h-8 w-8 border-4 border-purple-600 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : invitationRecords.length === 0 ? (
              <div className="bg-white shadow rounded-lg p-12 text-center">
                <EnvelopeIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 text-sm">No invitations found</p>
              </div>
            ) : (
              <div className="bg-white shadow rounded-lg overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-3 w-10">
                        <input
                          type="checkbox"
                          checked={selectedInvitationIds.size === invitationRecords.length && invitationRecords.length > 0}
                          onChange={handleSelectAllInvitations}
                          className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                        />
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Organization</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sent By</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sent Date</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Expires</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {invitationRecords.map((inv) => {
                      const statusColors: Record<string, string> = {
                        accepted: 'bg-green-100 text-green-800',
                        pending: 'bg-blue-100 text-blue-800',
                        expired: 'bg-amber-100 text-amber-800',
                        cancelled: 'bg-red-100 text-red-800',
                      };
                      return (
                        <tr key={inv.id} className="hover:bg-gray-50">
                          <td className="px-3 py-3">
                            <input
                              type="checkbox"
                              checked={selectedInvitationIds.has(inv.id)}
                              onChange={() => handleSelectInvitation(inv.id)}
                              className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                            />
                          </td>
                          <td className="px-3 py-3 text-sm text-gray-900">
                            <div className="max-w-[14rem] truncate" title={inv.email}>{inv.email}</div>
                          </td>
                          <td className="px-3 py-3">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getRoleBadgeClasses(inv.role)}`}>
                              {getRoleDisplayName(inv.role)}
                            </span>
                          </td>
                          <td className="px-3 py-3 text-sm text-gray-700">
                            <div className="max-w-[180px] truncate" title={inv.organizationName || undefined}>
                              {inv.organizationName || inv.organizationId || '-'}
                            </div>
                          </td>
                          <td className="px-3 py-3 text-sm text-gray-500">
                            {inv.invitedByName || '-'}
                          </td>
                          <td className="px-3 py-3 text-sm text-gray-500">
                            {inv.createdAt ? new Date(inv.createdAt).toLocaleDateString() : '-'}
                          </td>
                          <td className="px-3 py-3 text-sm text-gray-500">
                            {inv.expiresAt ? new Date(inv.expiresAt).toLocaleDateString() : '-'}
                          </td>
                          <td className="px-3 py-3">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColors[inv.status] || 'bg-gray-100 text-gray-800'}`}>
                              {inv.status.charAt(0).toUpperCase() + inv.status.slice(1)}
                            </span>
                          </td>
                          <td className="px-3 py-3">
                            <div className="flex items-center gap-1">
                              {/* Resend follows its endpoint's rule, which is wider than superadmin:
                                  platform.users.manage, then never an invitation above the caller's
                                  own role tier. Revive is a separate endpoint and stays platform
                                  work, so the two cannot share one control. On an expired
                                  invitation, callers without Revive get Resend: the resend endpoint
                                  sets it back to pending with a fresh expiration date. */}
                              {(inv.status === 'pending' || (inv.status === 'expired' && !isSuperAdmin())) &&
                                canResendInvitation({ role: user?.role, hasPermission }, inv.role) && (
                                <InvitationSendButton
                                  state={invitationSendState(inv.id)}
                                  titles={{
                                    idle: 'Resend invitation',
                                    sending: 'Resending invitation',
                                    sent: 'Invitation sent',
                                  }}
                                  onClick={() => handleResendInvitation(inv.id)}
                                />
                              )}
                              {isSuperAdmin() && inv.status === 'expired' && (
                                <InvitationSendButton
                                  state={invitationSendState(inv.id)}
                                  titles={{
                                    idle: 'Revive invitation',
                                    sending: 'Reviving invitation',
                                    sent: 'Invitation revived',
                                  }}
                                  onClick={() => handleReviveInvitation(inv.id)}
                                />
                              )}
                              {inv.status === 'pending' &&
                                inv.canManage !== false &&
                                canEditInvitation({ role: user?.role, hasPermission }, inv.role) && (
                                <button
                                  type="button"
                                  onClick={() => handleEditInvitation(inv)}
                                  title="Edit invitation"
                                  className="inline-flex items-center p-1.5 text-gray-600 hover:text-gray-800 hover:bg-gray-50 rounded"
                                >
                                  <PencilSquareIcon className="h-4 w-4" />
                                </button>
                              )}
                              {canCancelRecord(inv) && (
                                <button
                                  onClick={() => handleCancelInvitation(inv.id)}
                                  title="Cancel invitation"
                                  className="inline-flex items-center p-1.5 text-red-600 hover:text-red-800 hover:bg-red-50 rounded"
                                >
                                  <XCircleIcon className="h-4 w-4" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                {/* Pagination */}
                {invitationPagination.totalPages > 1 && (
                  <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
                    <p className="text-sm text-gray-700">
                      Showing {((invitationPagination.page - 1) * invitationPagination.limit) + 1}–{Math.min(invitationPagination.page * invitationPagination.limit, invitationPagination.total)} of {invitationPagination.total}
                    </p>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => { setInvitationPagination(prev => ({ ...prev, page: prev.page - 1 })); setSelectedInvitationIds(new Set()); }}
                        disabled={invitationPagination.page <= 1}
                        className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50 hover:bg-gray-50"
                      >
                        Previous
                      </button>
                      <span className="text-sm text-gray-700">
                        Page {invitationPagination.page} of {invitationPagination.totalPages}
                      </span>
                      <button
                        onClick={() => { setInvitationPagination(prev => ({ ...prev, page: prev.page + 1 })); setSelectedInvitationIds(new Set()); }}
                        disabled={invitationPagination.page >= invitationPagination.totalPages}
                        className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50 hover:bg-gray-50"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </TabsContent>

      </Tabs>

      {/* User Modal (Create/Edit) */}
      <UserModal
        mode={modalMode}
        user={editingUser}
        invitation={selectedInvitation}
        open={showModal}
        onOpenChange={setShowModal}
        onSave={handleSaveUser}
        organizations={organizations}
        enterprises={enterprises}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        title="Delete User"
        message={
          deletingUser ? (
            <>
              <p className="mb-2">
                Are you sure you want to delete <strong>{deletingUser.firstName} {deletingUser.lastName}</strong>?
              </p>
              <p className="text-sm text-gray-600">
                This will soft delete the user. The user will not be able to log in.
                This action can be reversed by reactivating the user.
              </p>
            </>
          ) : ''
        }
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleDeleteUser}
        variant="danger"
      />

      {/* Reactivate Confirmation Dialog */}
      <ConfirmationDialog
        open={showReactivateConfirm}
        onOpenChange={setShowReactivateConfirm}
        title="Reactivate User"
        message={
          reactivatingUser ? (
            <>
              <p className="mb-2">
                Are you sure you want to reactivate <strong>{reactivatingUser.firstName} {reactivatingUser.lastName}</strong>?
              </p>
              <p className="text-sm text-gray-600">
                The user will be able to log in again.
              </p>
            </>
          ) : ''
        }
        confirmText="Reactivate"
        cancelText="Cancel"
        onConfirm={handleReactivateUser}
        variant="info"
      />

      {/* Restore Deleted User Dialog */}
      {restoreUserInfo && (
        <RestoreUserDialog
          open={showRestoreDialog}
          onOpenChange={setShowRestoreDialog}
          userInfo={restoreUserInfo}
          onRestore={handleRestoreUser}
          isLoading={restoreLoading}
        />
      )}

      {/* Resend/Revive Invitation Dialog */}
      {resendInvitationInfo && (
        <ResendInvitationDialog
          open={showResendDialog}
          onOpenChange={setShowResendDialog}
          invitationInfo={resendInvitationInfo}
          onResend={handleResendFromDialog}
          onCancelInvitation={handleCancelFromDialog}
          canManage={canCancelInvitation({ role: user?.role, hasPermission }, resendInvitationInfo.role)}
          expiredActionLabel={isSuperAdmin() ? 'Revive & Send Invitation' : 'Send Invitation Again'}
          isLoading={resendLoading}
        />
      )}

      {/* Deactivation Confirmation Modal */}
      <DeactivateConfirmModal
        isOpen={deactivateTarget !== null}
        entityType="user"
        entityName={deactivateTarget?.email || ''}
        isLoading={isDeactivating}
        onConfirm={confirmDeactivateUser}
        // The modal leaves its backdrop, close button and Escape key live while loading, and a
        // dismissal mid-request would let the response close a later dialog.
        onCancel={() => {
          if (!isDeactivating) setDeactivateTarget(null);
        }}
      />

      {/* Audit History Timeline */}
      {showAuditHistory && auditUser && (
        <AuditHistoryTimeline
          userId={auditUser.id!}
          userName={`${auditUser.firstName} ${auditUser.lastName}`}
          onClose={() => {
            setShowAuditHistory(false);
            setAuditUser(null);
          }}
        />
      )}
    </PageTransition>
  );
};

export default PlatformUsers;
